import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Pencil, Trash2, Loader2, Star, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { PriceList, CreatePriceListData } from '../types/material.types';
import { materialService } from '../services/material-service';
import { PriceListEntriesEditor } from './price-list-entries-editor';

interface PriceListFormData {
  name: string;
  description: string;
  currency: string;
}

export function PriceListManager() {
  const { toast } = useToast();
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPriceList, setEditingPriceList] = useState<PriceList | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const [selectedPriceList, setSelectedPriceList] = useState<PriceList | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PriceListFormData>({
    defaultValues: { name: '', description: '', currency: 'USD' },
  });

  const fetchPriceLists = async () => {
    try {
      setIsLoading(true);
      const data = await materialService.getPriceLists();
      setPriceLists(data);
    } catch {
      toast({ title: 'Error al cargar listas de precios', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPriceLists();
  }, []);

  const openCreate = () => {
    reset({ name: '', description: '', currency: 'USD' });
    setEditingPriceList(null);
    setDialogOpen(true);
  };

  const openEdit = (pl: PriceList) => {
    reset({
      name: pl.name,
      description: pl.description || '',
      currency: pl.currency,
    });
    setEditingPriceList(pl);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingPriceList(null);
    reset();
  };

  const onSubmit = async (data: PriceListFormData) => {
    const payload: CreatePriceListData = {
      name: data.name,
      description: data.description || null,
      currency: data.currency || 'USD',
    };

    try {
      setIsSubmitting(true);
      if (editingPriceList) {
        const updated = await materialService.updatePriceList(editingPriceList.id, payload);
        setPriceLists((prev) => prev.map((pl) => (pl.id === editingPriceList.id ? updated : pl)));
        toast({ title: 'Lista de precios actualizada', variant: 'success' });
      } else {
        const created = await materialService.createPriceList(payload);
        setPriceLists((prev) => [...prev, created]);
        toast({ title: 'Lista de precios creada', variant: 'success' });
      }
      closeDialog();
    } catch {
      toast({ title: 'Error al guardar la lista de precios', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    const pl = priceLists.find((p) => p.id === id);
    if (!pl) return;

    if (pl.isDefault) {
      toast({
        title: 'No se puede eliminar la lista predeterminada',
        description: 'Defina otra lista como predeterminada primero.',
        variant: 'destructive',
      });
      setConfirmDeleteId(null);
      return;
    }

    try {
      setIsDeletingId(id);
      await materialService.deletePriceList(id);
      setPriceLists((prev) => prev.filter((p) => p.id !== id));
      toast({ title: 'Lista de precios eliminada', variant: 'success' });
    } catch {
      toast({ title: 'Error al eliminar la lista de precios', variant: 'destructive' });
    } finally {
      setIsDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (selectedPriceList) {
    return (
      <PriceListEntriesEditor
        priceList={selectedPriceList}
        onBack={() => {
          setSelectedPriceList(null);
          fetchPriceLists();
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Listas de Precios</h2>
          <p className="text-sm text-muted-foreground">
            Gestione las listas de precios de su catálogo de materiales.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Nueva lista
        </Button>
      </div>

      {priceLists.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">
          No hay listas de precios registradas.
        </p>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead>Moneda</TableHead>
                <TableHead className="text-right">Materiales</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {priceLists.map((pl) => (
                <TableRow key={pl.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span
                        className="font-medium text-primary hover:underline cursor-pointer"
                        onClick={() => setSelectedPriceList(pl)}
                      >
                        {pl.name}
                      </span>
                      {pl.isDefault && (
                        <Badge variant="secondary" className="gap-1">
                          <Star className="h-3 w-3" />
                          Predeterminada
                        </Badge>
                      )}
                      {!pl.isActive && <Badge variant="destructive">Inactiva</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {pl.description || '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{pl.currency}</Badge>
                  </TableCell>
                  <TableCell className="text-right text-sm">{pl._count?.entries ?? 0}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedPriceList(pl)}
                        disabled={!!isDeletingId}
                        title="Gestionar precios"
                      >
                        <DollarSign className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(pl)}
                        disabled={!!isDeletingId}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmDeleteId(pl.id)}
                        disabled={isDeletingId === pl.id || pl.isDefault}
                        className="text-destructive hover:text-destructive disabled:opacity-30"
                        title={
                          pl.isDefault ? 'No se puede eliminar la lista predeterminada' : 'Eliminar'
                        }
                      >
                        {isDeletingId === pl.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingPriceList ? 'Editar lista de precios' : 'Nueva lista de precios'}
            </DialogTitle>
            <DialogDescription>
              {editingPriceList
                ? 'Modifique los datos de la lista de precios.'
                : 'Complete los datos para crear una nueva lista de precios.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pl-name">Nombre *</Label>
              <Input
                id="pl-name"
                placeholder="Lista de precios general"
                {...register('name', { required: 'Nombre requerido' })}
                disabled={isSubmitting}
              />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="pl-description">Descripción</Label>
              <Textarea
                id="pl-description"
                rows={2}
                placeholder="Descripción de la lista de precios"
                {...register('description')}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pl-currency">Moneda</Label>
              <Input
                id="pl-currency"
                placeholder="USD"
                {...register('currency')}
                disabled={isSubmitting}
                className="uppercase"
              />
              <p className="text-xs text-muted-foreground">Ej: USD, EUR, COP, CRC</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingPriceList ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog confirmacion eliminar */}
      <Dialog open={!!confirmDeleteId} onOpenChange={(open) => !open && setConfirmDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar eliminación</DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer. Se eliminara la lista de precios y todos sus precios
              asociados.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmDeleteId && handleDelete(confirmDeleteId)}
              disabled={!!isDeletingId}
            >
              {isDeletingId ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PriceListManager;
